/**
 * StatusOverrideDialog
 *
 * Two-phase dialog for manually overriding order or garment production status.
 * Phase 0: Select target status from a grid of options.
 * Phase 1: Confirm with required reason (audit trail).
 *
 * Used by coordinators/admins to fix stuck orders or garments.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  RotateCcw,
  AlertCircle,
  Layers,
  Activity,
  Film,
  Flame,
  PackageCheck,
  Package,
  Truck,
  XCircle,
  Store,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useOverrideOrderStatus, useOverrideGarmentStatus } from '../hooks/useStatusOverride';
import { useRole } from '@/hooks/use-role';
import { isQcRedoNoRequeueRefusal } from '@/lib/trpc-error-utils';

/**
 * Order statuses a CX user may set. CX owns the customer conversation, not the
 * floor: New and Waiting for stock describe a customer-side decision, while
 * Shipped and Ready for pickup belong to Mark Shipped (which also writes the
 * Shopify fulfilment and notifies the customer), Cancelled is a one-way door,
 * and the in-production statuses describe where a garment physically is.
 *
 * ⚠ KEEP IN LOCKSTEP with CX_OVERRIDABLE_ORDER_STATUSES in
 * apps/backend/src/modules/production-orders/production-orders.types.ts, which
 * is what actually enforces this. Filtering here only keeps the dialog honest.
 */
const CX_ORDER_STATUS_VALUES = ['NEW', 'WAITING_STOCK'];

// ============================================================================
// TYPES
// ============================================================================

export type OverrideTarget =
  | { type: 'order'; orderId: string; orderName: string; currentStatus: string; locationId: string }
  | { type: 'garment'; garmentId: string; sku: string; currentStatus: string; orderId: string };

interface StatusOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: OverrideTarget;
}

// ============================================================================
// STATUS OPTION CONFIGS
// ============================================================================

interface StatusOption {
  value: string;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}

const ORDER_STATUS_OPTIONS: StatusOption[] = [
  { value: 'NEW', labelKey: 'tracker.statusOverride.options.new', descriptionKey: 'tracker.statusOverride.options.newDesc', icon: RotateCcw },
  { value: 'WAITING_STOCK', labelKey: 'tracker.statusOverride.options.waitingStock', descriptionKey: 'tracker.statusOverride.options.waitingStockDesc', icon: AlertCircle },
  { value: 'IN_BATCH', labelKey: 'tracker.statusOverride.options.inBatch', descriptionKey: 'tracker.statusOverride.options.inBatchDesc', icon: Layers },
  { value: 'IN_PROGRESS', labelKey: 'tracker.statusOverride.options.inProgress', descriptionKey: 'tracker.statusOverride.options.inProgressDesc', icon: Activity },
  { value: 'READY_TO_PACK', labelKey: 'tracker.statusOverride.options.readyToPack', descriptionKey: 'tracker.statusOverride.options.readyToPackDesc', icon: PackageCheck },
  { value: 'PACKED', labelKey: 'tracker.statusOverride.options.packed', descriptionKey: 'tracker.statusOverride.options.packedDesc', icon: Package },
  { value: 'SHIPPED', labelKey: 'tracker.statusOverride.options.shipped', descriptionKey: 'tracker.statusOverride.options.shippedDesc', icon: Truck },
  { value: 'READY_FOR_PICKUP', labelKey: 'tracker.statusOverride.options.readyForPickup', descriptionKey: 'tracker.statusOverride.options.readyForPickupDesc', icon: Store },
  { value: 'CANCELLED', labelKey: 'tracker.statusOverride.options.cancelled', descriptionKey: 'tracker.statusOverride.options.cancelledOrderDesc', icon: XCircle },
];

const GARMENT_STATUS_OPTIONS: StatusOption[] = [
  { value: 'PENDING', labelKey: 'tracker.statusOverride.options.pending', descriptionKey: 'tracker.statusOverride.options.pendingDesc', icon: RotateCcw },
  { value: 'LABEL_PRINTED', labelKey: 'tracker.statusOverride.options.labelPrinted', descriptionKey: 'tracker.statusOverride.options.labelPrintedDesc', icon: Layers },
  { value: 'PICKED', labelKey: 'tracker.statusOverride.options.picked', descriptionKey: 'tracker.statusOverride.options.pickedDesc', icon: PackageCheck },
  { value: 'IN_PRETREAT', labelKey: 'tracker.statusOverride.options.inPretreat', descriptionKey: 'tracker.statusOverride.options.inPretreatDesc', icon: Activity },
  { value: 'PRETREATED', labelKey: 'tracker.statusOverride.options.pretreated', descriptionKey: 'tracker.statusOverride.options.pretreatedDesc', icon: Activity },
  // Phase 50.01.5 — DTF garment states (between pretreated and caught).
  { value: 'FILM_PRINTED', labelKey: 'tracker.statusOverride.options.filmPrinted', descriptionKey: 'tracker.statusOverride.options.filmPrintedDesc', icon: Film },
  { value: 'PRESSED', labelKey: 'tracker.statusOverride.options.pressed', descriptionKey: 'tracker.statusOverride.options.pressedDesc', icon: Flame },
  { value: 'CAUGHT', labelKey: 'tracker.statusOverride.options.caught', descriptionKey: 'tracker.statusOverride.options.caughtDesc', icon: PackageCheck },
  { value: 'IN_BIN', labelKey: 'tracker.statusOverride.options.inBin', descriptionKey: 'tracker.statusOverride.options.inBinDesc', icon: Package },
  { value: 'READY_TO_PACK', labelKey: 'tracker.statusOverride.options.readyToPack', descriptionKey: 'tracker.statusOverride.options.readyToPackGarmentDesc', icon: PackageCheck },
  { value: 'RELEASED', labelKey: 'tracker.statusOverride.options.released', descriptionKey: 'tracker.statusOverride.options.releasedDesc', icon: PackageCheck },
  { value: 'PACKED', labelKey: 'tracker.statusOverride.options.packed', descriptionKey: 'tracker.statusOverride.options.packedShippingDesc', icon: Package },
  { value: 'SHIPPED', labelKey: 'tracker.statusOverride.options.shipped', descriptionKey: 'tracker.statusOverride.options.shippedGarmentDesc', icon: Truck },
  { value: 'CANCELLED', labelKey: 'tracker.statusOverride.options.cancelled', descriptionKey: 'tracker.statusOverride.options.cancelledGarmentDesc', icon: XCircle },
];

// Status badge colors — matched to TrackerGarmentTable + TrackerOrderTable for consistency
const statusColors: Record<string, string> = {
  // Order statuses (from TrackerOrderTable)
  NEW: 'bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-400',
  WAITING_STOCK: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  IN_BATCH: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  READY_TO_PACK: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  PACKED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  SHIPPED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  READY_FOR_PICKUP: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  CANCELLED: 'bg-slate-100 text-slate-600 dark:bg-slate-800/30 dark:text-slate-500',
  ON_HOLD: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  // Garment statuses (from TrackerGarmentTable)
  PENDING: 'bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-400',
  LABEL_PRINTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PICKED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  IN_PRETREAT: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  PRETREATED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  // Phase 50.01.5 — DTF (matches TrackerGarmentTable).
  FILM_PRINTED: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400',
  PRESSED: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-400',
  CAUGHT: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  IN_BIN: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  RELEASED: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
};

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ============================================================================
// COMPONENT
// ============================================================================

export function StatusOverrideDialog({ open, onOpenChange, target }: StatusOverrideDialogProps) {
  const { t } = useTranslation('production');
  const { isCX } = useRole();
  const [phase, setPhase] = useState<0 | 1>(0);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const orderMutation = useOverrideOrderStatus();
  const garmentMutation = useOverrideGarmentStatus();

  const isPending = orderMutation.isPending || garmentMutation.isPending;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setPhase(0);
      setSelectedStatus(null);
      setReason('');
    }
  }, [open]);

  const isOrder = target.type === 'order';
  const allOptions = isOrder ? ORDER_STATUS_OPTIONS : GARMENT_STATUS_OPTIONS;
  // filter-layer-ok: a fixed in-memory option list, not a paginated result set.
  const options =
    isOrder && isCX()
      ? allOptions.filter((opt) => CX_ORDER_STATUS_VALUES.includes(opt.value))
      : allOptions;
  const currentStatus = target.currentStatus;
  const entityName = isOrder ? target.orderName : target.sku;

  // Determine warning type
  const getWarning = () => {
    if (!selectedStatus) return null;

    if (selectedStatus === 'CANCELLED') {
      return {
        type: 'destructive' as const,
        message: isOrder
          ? t('tracker.statusOverride.warn.cancelOrder')
          : t('tracker.statusOverride.warn.cancelGarment'),
      };
    }

    // BLU-699: steer operators away from using Override Status to SHIP. Override
    // only flips the status — it does NOT create the Shopify fulfillment or notify
    // the customer (order 1061239 was left Unfulfilled in Shopify this way). The
    // "Mark Shipped" action does the proper ship (tags + fulfillment write-back).
    if (isOrder && (selectedStatus === 'SHIPPED' || selectedStatus === 'READY_FOR_PICKUP')) {
      return {
        type: 'warning' as const,
        message: t('tracker.statusOverride.warn.shipViaMarkShipped'),
      };
    }

    // Forward/backward detection
    const SEQUENCE = isOrder
      ? ['NEW', 'WAITING_STOCK', 'IN_BATCH', 'IN_PROGRESS', 'READY_TO_PACK', 'PACKED', 'SHIPPED', 'READY_FOR_PICKUP']
      // Phase 50.01.5 — DTF states inserted between PRETREATED and CAUGHT so the
      // forward/backward override warning indexes correctly for DTF garments.
      : ['PENDING', 'LABEL_PRINTED', 'PICKED', 'IN_PRETREAT', 'PRETREATED', 'FILM_PRINTED', 'PRESSED', 'CAUGHT', 'IN_BIN', 'READY_TO_PACK', 'RELEASED', 'PACKED', 'SHIPPED'];

    const currentIdx = SEQUENCE.indexOf(currentStatus);
    const targetIdx = SEQUENCE.indexOf(selectedStatus);
    if (currentIdx >= 0 && targetIdx >= 0) {
      if (targetIdx < currentIdx) {
        return {
          type: 'warning' as const,
          message: isOrder
            ? t('tracker.statusOverride.warn.revertOrder')
            : t('tracker.statusOverride.warn.revertGarment'),
        };
      }
      if (targetIdx > currentIdx + 1) {
        return {
          type: 'warning' as const,
          message: isOrder
            ? t('tracker.statusOverride.warn.skipOrder')
            : t('tracker.statusOverride.warn.skipGarment'),
        };
      }
    }

    return null;
  };

  const handleConfirm = async () => {
    if (!selectedStatus || !reason.trim()) return;

    try {
      if (target.type === 'order') {
        await orderMutation.mutateAsync({
          orderId: target.orderId,
          targetStatus: selectedStatus as never,
          reason: reason.trim(),
          locationId: target.locationId,
        });
        toast.success(t('tracker.statusOverride.statusUpdated'), {
          description: `${target.orderName}: ${formatStatus(currentStatus)} → ${formatStatus(selectedStatus)}`,
        });
      } else {
        await garmentMutation.mutateAsync({
          garmentId: target.garmentId,
          targetStatus: selectedStatus as never,
          reason: reason.trim(),
        });
        toast.success(t('tracker.statusOverride.statusUpdated'), {
          description: `${target.sku}: ${formatStatus(currentStatus)} → ${formatStatus(selectedStatus)}`,
        });
      }
      onOpenChange(false);
    } catch (error) {
      // BLU-1462: translated copy for the client QC redo refusal.
      const message = isQcRedoNoRequeueRefusal(error)
        ? t('tracker.statusOverride.refusedQcRedoRequeue')
        : error instanceof Error ? error.message : 'Failed to update status';
      toast.error(message);
    }
  };

  const warning = getWarning();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle>
            {t('tracker.statusOverride.overrideType', { type: isOrder ? t('tracker.statusOverride.order') : t('tracker.statusOverride.garment') })}
          </DialogTitle>
          <DialogDescription>
            {entityName}
          </DialogDescription>
        </DialogHeader>

        {phase === 0 ? (
          /* Phase 0: Status Selection */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {options.map((opt) => {
                const isCurrent = opt.value === currentStatus;
                const isSelected = opt.value === selectedStatus;
                const isDestructive = opt.value === 'CANCELLED';
                const Icon = opt.icon;

                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => setSelectedStatus(opt.value)}
                    className={cn(
                      'rounded-lg border p-3 min-h-[56px] text-left transition-colors',
                      'hover:bg-muted/50 cursor-pointer',
                      isCurrent && 'opacity-40 cursor-not-allowed',
                      isSelected && !isDestructive && 'border-primary bg-primary/5',
                      isSelected && isDestructive && 'border-red-400 bg-red-50 dark:bg-red-950/20',
                      !isSelected && isDestructive && 'border-red-200 hover:border-red-400',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={cn(
                        'h-4 w-4 shrink-0',
                        isDestructive ? 'text-red-500' : 'text-muted-foreground',
                      )} />
                      <div className="min-w-0">
                        <div className={cn(
                          'text-sm font-medium truncate',
                          isDestructive && 'text-red-700 dark:text-red-400',
                        )}>
                          {t(opt.labelKey)}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {isCurrent ? t('tracker.statusOverride.currentStatus') : t(opt.descriptionKey)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                className="h-12"
                onClick={() => onOpenChange(false)}
              >
                {t('tracker.statusOverride.cancel')}
              </Button>
              <Button
                className="h-12"
                disabled={!selectedStatus}
                onClick={() => setPhase(1)}
              >
                {t('tracker.statusOverride.next')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* Phase 1: Confirm + Reason */
          <div className="space-y-4">
            {/* Transition visual */}
            <div className="flex items-center justify-center gap-3 py-2">
              <Badge
                variant="secondary"
                className={cn('text-xs', statusColors[currentStatus] ?? statusColors.NEW)}
              >
                {formatStatus(currentStatus)}
              </Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              <Badge
                variant="secondary"
                className={cn('text-xs', statusColors[selectedStatus!] ?? statusColors.NEW)}
              >
                {formatStatus(selectedStatus!)}
              </Badge>
            </div>

            {/* Warning banner */}
            {warning && (
              <div className={cn(
                'flex items-start gap-2 rounded-md border p-3 text-sm',
                warning.type === 'destructive'
                  ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300'
                  : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300',
              )}>
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{warning.message}</span>
              </div>
            )}

            {/* Reason textarea */}
            <div className="space-y-2">
              <label htmlFor="override-reason" className="text-sm font-medium">
                {t('tracker.statusOverride.reasonLabel')} <span className="text-red-500">*</span>
              </label>
              <Textarea
                id="override-reason"
                placeholder={t('tracker.statusOverride.reasonPlaceholder')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[100px] text-base"
                maxLength={500}
                autoFocus
              />
              <div className="text-[11px] text-muted-foreground text-right">
                {reason.length}/500
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                className="h-12"
                onClick={() => setPhase(0)}
                disabled={isPending}
              >
                {t('tracker.statusOverride.back')}
              </Button>
              <Button
                className="h-12"
                variant={selectedStatus === 'CANCELLED' ? 'destructive' : 'default'}
                disabled={!reason.trim() || isPending}
                onClick={handleConfirm}
              >
                {isPending ? t('tracker.statusOverride.updating') : t('tracker.statusOverride.overrideBtn')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
